import type {CSSProperties} from 'react';
import {radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeSavedTrayLook = 'saved-tray-tag-remove' | 'saved-tray-suggest' | 'saved-tray-bar' | 'saved-tray-bar-danger' | 'saved-tray-undo' | 'saved-tray-dismiss'
  | 'saved-tray-menu-row' | 'saved-tray-menu-row-danger' | 'saved-tray-menu-cancel' | 'saved-tray-peer' | 'saved-tray-peer-cancel';
export type WedgeSavedTrayFieldVariant = 'wedge-saved-tray-field' | 'wedge-saved-tray-note';

const TRAY_LOOKS = new Set<string>(['saved-tray-tag-remove', 'saved-tray-suggest', 'saved-tray-bar', 'saved-tray-bar-danger', 'saved-tray-undo', 'saved-tray-dismiss',
  'saved-tray-menu-row', 'saved-tray-menu-row-danger', 'saved-tray-menu-cancel', 'saved-tray-peer', 'saved-tray-peer-cancel']);
export const isWedgeSavedTrayLook = (look: string): look is WedgeSavedTrayLook => TRAY_LOOKS.has(look);
export const isWedgeSavedTrayFieldVariant = (variant: unknown): variant is WedgeSavedTrayFieldVariant => variant === 'wedge-saved-tray-field' || variant === 'wedge-saved-tray-note';

/* Released Saved tag tray, select toolbar and undo pill: five native buttons and one native field, all in the Wedge font. Every box here is
   a shipped inline style, off-scale numbers included; each color reads the exact legacy role, so the shipped ink rules keep matching. The
   remove mark of a tag chip is an 18px round bare button in the accent ink. A suggestion is an inline-flex pill (4px gap, 6px by 11px
   padding, 12px semibold secondary type) on the surface fill with a 1px card border. A toolbar action is a flex-1 column (3px gap, 6px by
   4px padding) with the accent ink, or the red ink for Delete; with nothing selected it takes 35% opacity, the default cursor and no
   pointer events. Undo is a 7px by 14px accent-tint pill (13px heavy type) and the dismiss mark a 30px round bare button in the tertiary
   ink. Each takes the tap highlight off. The tag field is one bare native input (full width, 12px by 56px by 12px by 13px padding for the
   counter that sits over its right end, 11px radius, the elevated fill, a 1px card border, no outline, 16px type, which keeps iOS from
   zooming). The caller owns the glyphs, the labels, the margins and the handlers.
   The long-press menu and the handoff picker add five more buttons and one field. A menu row is a full-width flex row (12px gap, 13px by 14px
   padding, 11px radius, 48px high at least, 15px semibold type) on no fill, in the label ink or the red ink for Delete; its caller draws the
   icon span and the label. The menu Cancel is a block (8px above, 14px padding, 20px radius, the elevated fill, a 1px card border, 15px bold
   type). A picker row is a full-width flex row (11px gap, 12px by 16px padding) with a 0.5px separator on top; while a send is in flight it
   takes the default cursor and 55% opacity. The picker Cancel is a bare block (14px padding, 0.5px separator on top, 14.5px bold type). The
   note field is a bare native input (full width, 10px above, 11px by 12px padding, 11px radius, the surface fill, a 1px card border, 13.5px
   type, no outline).
   No tray look draws a transparent target (wedgeSavedTrayDrawsHit), as legacy: the 18px remove mark sits inside its chip and the suggestion
   pills wrap 6px apart, so their regions would overlap a neighbor; the toolbar actions are about 48px tall and share the width of the bar,
   so a region could only repeat the box; Undo and the round 30px dismiss mark sit 10px apart in the pill, and a square region would
   undercut the round mark. The raster effect of an anchor on Undo and dismiss was not measured in a browser. */
const TAP = {WebkitTapHighlightColor: 'transparent'} as const;

export const wedgeSavedTrayDrawsHit = (look: string): boolean => !look.startsWith('saved-tray-');

export function wedgeSavedTrayStyle(look: WedgeSavedTrayLook, disabled = false): CSSProperties {
  const r = wedgeExactRole, fontFamily = r('font');
  switch (look) {
    case 'saved-tray-tag-remove': return {
      width: 18, height: 18, borderRadius: radius.pill, background: 'transparent', border: 0, cursor: 'pointer', display: 'flex',
      alignItems: 'center', justifyContent: 'center', color: r('accent'),
    };
    case 'saved-tray-suggest': return {
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: radius.pill, background: r('surface'),
      color: r('labelSec'), border: `1px solid ${r('cardBorder')}`, cursor: 'pointer', fontFamily, fontSize: 12, fontWeight: weight.semibold,
    };
    case 'saved-tray-bar':
    case 'saved-tray-bar-danger': return {
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '6px 4px', background: 'transparent', border: 0,
      cursor: disabled ? 'default' : 'pointer', fontFamily, color: look === 'saved-tray-bar-danger' ? r('red') : r('accent'),
      ...(disabled ? {opacity: 0.35, pointerEvents: 'none'} : {}), ...TAP,
    };
    case 'saved-tray-undo': return {
      flexShrink: 0, padding: '7px 14px', borderRadius: radius.pill, background: r('accentSoft'), color: r('accent'), border: 0, cursor: 'pointer',
      fontFamily, fontSize: 13, fontWeight: weight.heavy, ...TAP,
    };
    case 'saved-tray-menu-row':
    case 'saved-tray-menu-row-danger': return {
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '13px 14px', borderRadius: 11, minHeight: 48, background: 'transparent', border: 0,
      cursor: 'pointer', textAlign: 'left', fontFamily, fontSize: 15, fontWeight: weight.semibold, color: look === 'saved-tray-menu-row-danger' ? r('red') : r('label'), ...TAP,
    };
    case 'saved-tray-menu-cancel': return {
      display: 'block', width: '100%', marginTop: 8, padding: 14, borderRadius: 20, background: r('bgElev'), border: `1px solid ${r('cardBorder')}`, color: r('label'),
      cursor: 'pointer', fontFamily, fontSize: 15, fontWeight: weight.bold,
    };
    case 'saved-tray-peer': return {
      display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '12px 16px', background: 'transparent', border: 0, borderTop: `0.5px solid ${r('separator')}`,
      cursor: disabled ? 'default' : 'pointer', textAlign: 'left', fontFamily, opacity: disabled ? 0.55 : 1, ...TAP,
    };
    case 'saved-tray-peer-cancel': return {
      display: 'block', width: '100%', padding: 14, background: 'transparent', border: 0, borderTop: `0.5px solid ${r('separator')}`, color: r('label'), cursor: 'pointer',
      fontFamily, fontSize: 14.5, fontWeight: weight.bold,
    };
    case 'saved-tray-dismiss': return {
      flexShrink: 0, width: 30, height: 30, borderRadius: radius.pill, background: 'transparent', border: 0, cursor: 'pointer', display: 'flex',
      alignItems: 'center', justifyContent: 'center', color: r('labelTer'), ...TAP,
    };
  }
}

export function wedgeSavedTrayFieldStyle(variant: WedgeSavedTrayFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  if (variant === 'wedge-saved-tray-note') return {
    width: '100%', marginTop: 10, padding: '11px 12px', borderRadius: 11, background: r('surface'), border: `1px solid ${r('cardBorder')}`, fontSize: 13.5,
    color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
  };
  return {
    width: '100%', boxSizing: 'border-box', padding: '12px 56px 12px 13px', borderRadius: 11, background: r('bgElev'), color: r('label'),
    border: `1px solid ${r('cardBorder')}`, outline: 'none', fontFamily: r('font'), fontSize: 16,
  };
}
